import { z } from 'zod'
import { toast } from 'sonner'
import { useState } from 'react'
import { paths } from '@/routes/paths'
import { useForm } from 'react-hook-form'
import { uploadFile } from '@/lib/firebase'
import { fError } from '@/utils/format-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useBoolean } from '@/hooks/use-boolean'
import { Plus, X, Calendar } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from '@/routes/hooks/use-router'
import RichTextEditor from '@/components/ui/rich-text-editor'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  formatForDateTimeLocal,
  parseFromDateTimeLocal,
  now,
} from '@/utils/dayjs'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import type { Program } from './program.types'

// File upload type
type FileUpload = {
  url: File | string
  fileName: string
}

// Schema definition
const programFormSchema = z.object({
  title: z.string().min(1, 'กรุณาป้อนชื่อโปรแกรม'),
  description: z.string().min(1, 'กรุณาป้อนรายละเอียดโปรแกรม'),
  tags: z.array(z.string()),
  isEnabled: z.boolean(),
  isPublic: z.boolean(),
  isCommentActive: z.boolean(),
  publishedAt: z.string().optional(),
})

type FormData = z.infer<typeof programFormSchema>

interface ProgramFormProps {
  program?: Program // For edit mode
  mode: 'create' | 'edit'
}

export default function ProgramForm({ program, mode }: ProgramFormProps) {
  const router = useRouter()
  const submitting = useBoolean(false)

  const [newTag, setNewTag] = useState<string>('')

  const [thumbnailImage, setThumbnailImage] = useState<FileUpload | null>(
    program?.thumbnailUrl
      ? {
          url: program.thumbnailUrl,
          fileName: 'thumbnail-image',
        }
      : null,
  )

  const form = useForm<FormData>({
    resolver: zodResolver(programFormSchema),
    defaultValues: {
      title: program?.title || '',
      description: program?.description || '',
      tags: program?.tags || [],
      isEnabled: program?.isEnabled ?? true,
      isPublic: program?.isPublic ?? false,
      isCommentActive: program?.isCommentActive ?? false,
      publishedAt: program?.publishedAt
        ? formatForDateTimeLocal(program.publishedAt)
        : undefined,
    },
  })

  const handleThumbnailImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (file) {
      setThumbnailImage({
        url: file,
        fileName: file.name,
      })
    }
  }

  const handleAddTag = () => {
    if (newTag.trim() && !form.getValues('tags').includes(newTag.trim())) {
      const currentTags = form.getValues('tags')
      form.setValue('tags', [...currentTags, newTag.trim()])
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = form.getValues('tags')
    form.setValue(
      'tags',
      currentTags.filter((tag) => tag !== tagToRemove),
    )
  }

  const onSubmit = async (data: FormData) => {
    try {
      submitting.onTrue()

      // Upload thumbnail image
      let thumbnailUrl = ''
      if (thumbnailImage) {
        if (typeof thumbnailImage.url === 'string') {
          thumbnailUrl = thumbnailImage.url
        } else {
          thumbnailUrl = await uploadFile(
            thumbnailImage.url,
            'programs/thumbnails',
            thumbnailImage.fileName,
          )
        }
      }

      // Convert publishedAt string to ISO string for API
      const apiData = {
        ...data,
        thumbnailUrl,
        publishedAt: data.publishedAt
          ? parseFromDateTimeLocal(data.publishedAt)?.toISOString()
          : undefined,
      }

      if (mode === 'create') {
        await axios.post(endpoints.programs.create, apiData)
        toast.success('สร้างโปรแกรมสำเร็จ')
        router.replace(paths.programs)
      } else {
        await axios.patch(endpoints.programs.update(program!.id), apiData)
        toast.success('อัปเดตโปรแกรมสำเร็จ')
        router.reload()
      }
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      submitting.onFalse()
    }
  }

  const handleBack = () => {
    router.push(paths.programs)
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <Form {...form}>
        <form
          id="program-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4 sm:space-y-6"
        >
          {/* Main Layout: Stack on mobile, side by side on desktop */}
          {/* Main Form */}
          <div className="xl:col-span-2 space-y-4 sm:space-y-6">
            <Card className="border rounded-lg space-y-0">
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="text-base sm:text-lg font-semibold text-black">
                  ข้อมูลโปรแกรม
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 pt-0 space-y-4 sm:space-y-6">
                {/* Title */}
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base font-medium text-black">
                        ชื่อโปรแกรม *
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="กรอกชื่อโปรแกรม"
                          className="rounded-lg border h-10 sm:h-11"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base font-medium text-black">
                        รายละเอียด *
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="กรอกรายละเอียดโปรแกรม"
                          className="min-h-[150px] sm:min-h-[200px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Thumbnail Image */}
                <div className="space-y-2">
                  <Label className="text-sm sm:text-base font-medium text-black">
                    รูปภาพปก *
                  </Label>
                  <div className="space-y-3">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailImageChange}
                      className="rounded-lg border h-10 sm:h-11"
                    />
                    {thumbnailImage && (
                      <div className="relative inline-block">
                        <img
                          src={
                            typeof thumbnailImage.url === 'string'
                              ? thumbnailImage.url
                              : URL.createObjectURL(thumbnailImage.url)
                          }
                          alt="Thumbnail"
                          className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute -top-2 -right-2 h-5 w-5 sm:h-6 sm:w-6 rounded-full p-0"
                          onClick={() => setThumbnailImage(null)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Published At */}
                <FormField
                  control={form.control}
                  name="publishedAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm sm:text-base font-medium text-black">
                        วันที่เผยแพร่
                      </FormLabel>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <FormControl>
                          <Input
                            {...field}
                            type="datetime-local"
                            className="flex-1 rounded-lg border h-10 sm:h-11"
                          />
                        </FormControl>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              field.onChange(formatForDateTimeLocal(now()))
                            }
                            className="flex-1 sm:flex-none h-9 sm:h-9"
                          >
                            <Calendar className="h-4 w-4 mr-1 sm:mr-0" />
                            <span className="sm:hidden">ตอนนี้</span>
                          </Button>
                          {field.value && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => field.onChange(undefined)}
                              className="flex-1 sm:flex-none h-9 sm:h-9"
                            >
                              ล้าง
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        ปล่อยว่างไว้หากยังไม่ต้องการเผยแพร่
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Tags */}
                <div className="space-y-3 sm:space-y-4">
                  <Label className="text-sm sm:text-base font-medium text-black">
                    แท็ก
                  </Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="เพิ่มแท็กใหม่"
                      className="rounded-lg border h-10 sm:h-11 flex-1"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddTag()
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={handleAddTag}
                      disabled={!newTag.trim()}
                      variant="outline"
                      className="h-10 sm:h-11 w-full sm:w-auto"
                    >
                      <Plus className="h-4 w-4 mr-1 sm:mr-0" />
                      <span className="sm:hidden">เพิ่มแท็ก</span>
                    </Button>
                  </div>

                  {form.watch('tags').length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {form.watch('tags').map((tag, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="gap-1 text-xs"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Settings */}
                <div className="space-y-3 sm:space-y-4">
                  <Label className="text-sm sm:text-base font-medium text-black">
                    การตั้งค่า
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 rounded-lg border p-3 sm:p-4">
                    <FormField
                      control={form.control}
                      name="isEnabled"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2 space-y-0">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-medium text-black text-sm">
                            เปิดใช้งานโปรแกรม
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="isPublic"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2 space-y-0">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-medium text-black text-sm">
                            แชร์สาธารณะ
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="isCommentActive"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2 space-y-0">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-medium text-black text-sm">
                            เปิดคอมเม้นต์
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-3 py-4 sm:py-6 border-t">
            <Button
              type="submit"
              form="program-form"
              disabled={submitting.value}
              className="w-full sm:w-auto h-10 sm:h-11 order-2 sm:order-1"
            >
              {submitting.value
                ? 'กำลังบันทึก...'
                : mode === 'edit'
                  ? 'แก้ไข'
                  : 'สร้าง'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={submitting.value}
              className="w-full sm:w-auto h-10 sm:h-11 order-1 sm:order-2"
            >
              ยกเลิก
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
